import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TrendUp, Plus, Users, Sparkle, PaperPlaneTilt, CheckCircle, XCircle, ArrowsClockwise, Trash, Pencil, Coins, ChartBar, Trophy, Warning } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const INR = (n) => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`;
const ANNUAL = (n) => n == null ? "—" : `₹${Math.round(Number(n) * 12).toLocaleString("en-IN")}`;

const STATUS_TONES = {
  draft: "bg-zinc-100 text-zinc-800 border-zinc-300",
  in_review: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-blue-100 text-blue-800 border-blue-300",
  published: "bg-emerald-100 text-emerald-800 border-emerald-300",
  cancelled: "bg-rose-100 text-rose-800 border-rose-300",
};

const ITEM_TONES = {
  pending: "bg-zinc-100 text-zinc-700 border-zinc-300",
  manager_approved: "bg-blue-100 text-blue-800 border-blue-300",
  hr_approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
};

// ============================================================
// List page
// ============================================================
function CycleList() {
  const nav = useNavigate();
  const { user, tenantId } = useAuth();
  const [cycles, setCycles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", effective_date: "", grade_bands: [{ grade: "L3", default_percent: 8, min_percent: 0, max_percent: 30 }, { grade: "L4", default_percent: 10, min_percent: 0, max_percent: 30 }, { grade: "M2", default_percent: 12, min_percent: 0, max_percent: 30 }], rating_multipliers: [{ rating: "Exceeds", bonus_percent: 5 }, { rating: "Meets", bonus_percent: 0 }, { rating: "Below", bonus_percent: -3 }], notes: "" });
  const params = useMemo(() => (user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {}), [user?.role, tenantId]);

  const load = async () => {
    try {
      const { data } = await api.get("/increment-cycles", { params });
      setCycles(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const create = async () => {
    if (!form.name || !form.effective_date) return toast.error("Name and effective date are required");
    try {
      const { data } = await api.post("/increment-cycles", { ...params, ...form });
      toast.success("Cycle created");
      setOpen(false);
      nav(`/app/increment-cycles/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="increment-cycles-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-adb-navy">Compensation · Annual cycle</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Increment cycles</h1>
          <p className="text-sm text-zinc-500 mt-2">Run a whole appraisal cycle end-to-end — per-grade % suggestions, manager review, HR sign-off, and one-click letters.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="new-cycle-btn">
          <Plus size={16} className="mr-1.5" /> New cycle
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cycles-list">
        {cycles.length === 0 && (
          <div className="col-span-full text-center py-16 text-zinc-500 text-sm border border-dashed border-zinc-300 rounded-sm">
            No cycles yet. Create your first annual cycle to get started.
          </div>
        )}
        {cycles.map((c) => (
          <Link key={c.id} to={`/app/increment-cycles/${c.id}`} className="border border-zinc-200 rounded-sm p-4 bg-white hover:border-adb-navy hover:shadow-sm transition-all">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold">{c.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">Effective: {c.effective_date}</div>
              </div>
              <Badge variant="outline" className={`rounded-sm text-[10px] ${STATUS_TONES[c.status] || STATUS_TONES.draft}`}>{c.status}</Badge>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
              <div><div className="font-bold text-zinc-800">{c.counts?.total || 0}</div><div className="text-zinc-500">total</div></div>
              <div><div className="font-bold text-blue-700">{c.counts?.manager_approved || 0}</div><div className="text-zinc-500">mgr ok</div></div>
              <div><div className="font-bold text-emerald-700">{c.counts?.hr_approved || 0}</div><div className="text-zinc-500">hr ok</div></div>
              <div><div className="font-bold text-rose-600">{c.counts?.rejected || 0}</div><div className="text-zinc-500">rejected</div></div>
            </div>
          </Link>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New increment cycle</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs">Cycle name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="FY 26-27 Annual Increment" className="rounded-sm" data-testid="cycle-name-input" /></div>
              <div><Label className="text-xs">Effective date</Label><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="rounded-sm" data-testid="cycle-effective-date" /></div>
            </div>
            <div>
              <Label className="text-xs">Per-grade default % (drag or edit)</Label>
              <div className="mt-1 border border-zinc-200 rounded-sm">
                <table className="hr-table">
                  <thead><tr><th>Grade</th><th className="text-right">Default %</th><th className="text-right">Min %</th><th className="text-right">Max %</th><th></th></tr></thead>
                  <tbody>
                    {form.grade_bands.map((b, i) => (
                      <tr key={i}>
                        <td><Input value={b.grade} onChange={(e) => { const g = [...form.grade_bands]; g[i].grade = e.target.value; setForm({ ...form, grade_bands: g }); }} className="rounded-sm h-8 max-w-[80px]" /></td>
                        <td><Input type="number" step="0.5" value={b.default_percent} onChange={(e) => { const g = [...form.grade_bands]; g[i].default_percent = parseFloat(e.target.value); setForm({ ...form, grade_bands: g }); }} className="rounded-sm h-8 max-w-[80px] ml-auto text-right" /></td>
                        <td><Input type="number" value={b.min_percent} onChange={(e) => { const g = [...form.grade_bands]; g[i].min_percent = parseFloat(e.target.value); setForm({ ...form, grade_bands: g }); }} className="rounded-sm h-8 max-w-[70px] ml-auto text-right" /></td>
                        <td><Input type="number" value={b.max_percent} onChange={(e) => { const g = [...form.grade_bands]; g[i].max_percent = parseFloat(e.target.value); setForm({ ...form, grade_bands: g }); }} className="rounded-sm h-8 max-w-[70px] ml-auto text-right" /></td>
                        <td><button onClick={() => setForm({ ...form, grade_bands: form.grade_bands.filter((_, x) => x !== i) })} className="text-zinc-400 hover:text-rose-600"><Trash size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button variant="outline" size="sm" onClick={() => setForm({ ...form, grade_bands: [...form.grade_bands, { grade: "", default_percent: 8, min_percent: 0, max_percent: 30 }] })} className="rounded-sm mt-2"><Plus size={14} className="mr-1" /> Add grade</Button>
            </div>
            <div>
              <Label className="text-xs">Merit rating multipliers (added to grade % based on rating)</Label>
              <div className="mt-1 border border-zinc-200 rounded-sm">
                <table className="hr-table">
                  <thead><tr><th>Rating</th><th className="text-right">Bonus %</th><th></th></tr></thead>
                  <tbody>
                    {form.rating_multipliers.map((rm, i) => (
                      <tr key={i}>
                        <td><Input value={rm.rating} onChange={(e) => { const g = [...form.rating_multipliers]; g[i].rating = e.target.value; setForm({ ...form, rating_multipliers: g }); }} className="rounded-sm h-8 max-w-[120px]" /></td>
                        <td><Input type="number" step="0.5" value={rm.bonus_percent} onChange={(e) => { const g = [...form.rating_multipliers]; g[i].bonus_percent = parseFloat(e.target.value); setForm({ ...form, rating_multipliers: g }); }} className="rounded-sm h-8 max-w-[80px] ml-auto text-right" /></td>
                        <td><button onClick={() => setForm({ ...form, rating_multipliers: form.rating_multipliers.filter((_, x) => x !== i) })} className="text-zinc-400 hover:text-rose-600"><Trash size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button variant="outline" size="sm" onClick={() => setForm({ ...form, rating_multipliers: [...form.rating_multipliers, { rating: "", bonus_percent: 0 }] })} className="rounded-sm mt-2"><Plus size={14} className="mr-1" /> Add rating</Button>
            </div>
            <div><Label className="text-xs">Notes (optional)</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={create} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="create-cycle-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Detail wizard
// ============================================================
function CycleDetail({ cid }) {
  const { user } = useAuth();
  const isHR = ["super_admin", "company_admin", "hr"].includes(user?.role);
  const [cycle, setCycle] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ department: "", grade: "", status: "", q: "" });
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({});
  const [tab, setTab] = useState("review");
  const [analytics, setAnalytics] = useState(null);
  const [priorCycles, setPriorCycles] = useState([]);
  const [comparePrevId, setComparePrevId] = useState("");
  const [comparison, setComparison] = useState(null);

  const RATINGS = cycle?.rating_multipliers?.map((r) => r.rating).filter(Boolean) || [];

  const loadAnalytics = async () => {
    try {
      const { data } = await api.get(`/increment-cycles/${cid}/analytics`);
      setAnalytics(data);
    } catch (e) { /* silent */ }
  };
  const loadPriorCycles = async () => {
    try {
      const { data } = await api.get(`/increment-cycles/${cid}/prior-cycles`);
      setPriorCycles(data);
    } catch (e) { /* silent */ }
  };
  const loadComparison = async (prevId) => {
    if (!prevId) { setComparison(null); return; }
    try {
      const { data } = await api.get(`/increment-cycles/${cid}/comparison`, { params: { prev_cid: prevId } });
      setComparison(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadPriorCycles(); /* eslint-disable-next-line */ }, [cid]);
  useEffect(() => { if (comparePrevId) loadComparison(comparePrevId); else setComparison(null); /* eslint-disable-next-line */ }, [comparePrevId]);
  useEffect(() => { if (tab === "analytics") loadAnalytics(); /* eslint-disable-next-line */ }, [tab, items.length]);


  const load = async () => {
    try {
      const [c, its] = await Promise.all([
        api.get(`/increment-cycles/${cid}`).then((r) => r.data),
        api.get(`/increment-cycles/${cid}/items`, { params: filter }).then((r) => r.data),
      ]);
      setCycle(c);
      setItems(its);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid, filter]);
  useEffect(() => { if (cycle?.status === "draft") setTab("setup"); }, [cycle?.status]);

  const populate = async () => {
    if (!window.confirm("Pull all active employees and generate proposals? Any existing items will be overwritten.")) return;
    setBusy(true);
    try {
      await api.post(`/increment-cycles/${cid}/populate`, {});
      toast.success("Populated");
      await load();
      setTab("review");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const patchItem = async (iid, patch) => {
    try {
      await api.patch(`/increment-cycles/${cid}/items/${iid}`, patch);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulk = async (action) => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return toast.error("Pick at least one item");
    const reason = action === "reject" ? window.prompt("Rejection reason (optional):") : null;
    try {
      const { data } = await api.post(`/increment-cycles/${cid}/items/bulk-action`, { item_ids: ids, action, reason });
      toast.success(`${data.updated} item(s) updated`);
      setSelected({});
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const publish = async () => {
    const okCount = items.filter((i) => i.status === "hr_approved").length;
    if (okCount === 0) return toast.error("Approve at least one item first");
    if (!window.confirm(`Publish ${okCount} approved increment(s)? New salary structures will take effect on ${cycle.effective_date}.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/increment-cycles/${cid}/publish`, {});
      toast.success(`Published ${data.published} new salary structures`);
      await load();
      setTab("letters");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const issueLetters = async () => {
    if (!window.confirm("Send salary revision letters to all HR-approved employees? (PDF + email + in-app notification)")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/increment-cycles/${cid}/issue-letters`, {
        template_key: "salary_revision", email: true, save_pdf: true, notification: true,
      });
      toast.success(`Letters issued: ${data.counts.generated} · Emails: ${data.counts.email_sent} · Notifications: ${data.counts.notified}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const totals = useMemo(() => {
    if (!items.length) return null;
    const cur = items.reduce((s, i) => s + (i.current_ctc || 0), 0);
    const proposed = items.reduce((s, i) => s + (i.proposed_new_ctc || 0), 0);
    const delta = proposed - cur;
    const pct = cur ? (delta / cur) * 100 : 0;
    return { cur, proposed, delta, pct };
  }, [items]);

  const uniqueDepts = [...new Set(items.map((i) => i.department).filter(Boolean))];
  const uniqueGrades = [...new Set(items.map((i) => i.grade).filter(Boolean))];
  const toggleAll = (val) => { const s = {}; for (const it of items) s[it.id] = val; setSelected(s); };

  if (!cycle) return <div className="text-center py-16 text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="cycle-detail-page">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/app/increment-cycles" className="text-xs text-adb-navy hover:underline">← All cycles</Link>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">{cycle.name}</h1>
          <p className="text-sm text-zinc-500 mt-1">Effective from {cycle.effective_date}</p>
        </div>
        <Badge variant="outline" className={`rounded-sm ${STATUS_TONES[cycle.status]}`} data-testid="cycle-status-chip">{cycle.status}</Badge>
      </div>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border border-zinc-200 rounded-sm p-3 bg-white"><div className="text-[11px] uppercase tracking-wider text-zinc-500">Current CTC (mo)</div><div className="font-display text-xl font-bold">{INR(totals.cur)}</div></div>
          <div className="border border-zinc-200 rounded-sm p-3 bg-white"><div className="text-[11px] uppercase tracking-wider text-zinc-500">Proposed CTC (mo)</div><div className="font-display text-xl font-bold text-adb-navy">{INR(totals.proposed)}</div></div>
          <div className="border border-emerald-300 rounded-sm p-3 bg-emerald-50"><div className="text-[11px] uppercase tracking-wider text-emerald-700">Delta (mo)</div><div className="font-display text-xl font-bold text-emerald-800">{INR(totals.delta)}</div></div>
          <div className="border border-emerald-300 rounded-sm p-3 bg-emerald-50"><div className="text-[11px] uppercase tracking-wider text-emerald-700">Avg hike</div><div className="font-display text-xl font-bold text-emerald-800">{totals.pct.toFixed(1)}%</div></div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="setup" className="rounded-sm" data-testid="tab-setup">1. Setup</TabsTrigger>
          <TabsTrigger value="review" className="rounded-sm" data-testid="tab-review" disabled={cycle.status === "draft"}>2. Review &amp; approve</TabsTrigger>
          <TabsTrigger value="analytics" className="rounded-sm" data-testid="tab-analytics" disabled={cycle.status === "draft"}>3. Analytics</TabsTrigger>
          <TabsTrigger value="publish" className="rounded-sm" data-testid="tab-publish" disabled={!["in_review", "approved", "published"].includes(cycle.status)}>4. Publish</TabsTrigger>
          <TabsTrigger value="letters" className="rounded-sm" data-testid="tab-letters" disabled={cycle.status !== "published"}>5. Issue letters</TabsTrigger>
        </TabsList>

        {/* Setup */}
        <TabsContent value="setup" className="mt-4">
          <div className="border border-zinc-200 rounded-sm p-4 bg-white space-y-3">
            <div className="text-sm font-semibold">Grade bands</div>
            <table className="hr-table">
              <thead><tr><th>Grade</th><th className="text-right">Default %</th><th className="text-right">Min</th><th className="text-right">Max</th></tr></thead>
              <tbody>
                {cycle.grade_bands?.map((b, i) => (
                  <tr key={i}>
                    <td className="font-mono">{b.grade}</td>
                    <td className="text-right font-bold text-adb-navy">{b.default_percent}%</td>
                    <td className="text-right text-zinc-500">{b.min_percent}%</td>
                    <td className="text-right text-zinc-500">{b.max_percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {cycle.status === "draft" && (
              <Button onClick={populate} disabled={busy} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm mt-2" data-testid="populate-btn">
                <Sparkle size={16} className="mr-1.5" />
                {busy ? "Populating…" : "Populate proposals"}
              </Button>
            )}
            {cycle.status !== "draft" && (
              <Button variant="outline" onClick={populate} disabled={busy} className="rounded-sm mt-2"><ArrowsClockwise size={14} className="mr-1" /> Re-populate</Button>
            )}
          </div>
          {cycle.rating_multipliers?.length > 0 && (
            <div className="border border-zinc-200 rounded-sm p-4 bg-white mt-4">
              <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Trophy size={16} className="text-amber-600" /> Merit rating multipliers</div>
              <div className="text-xs text-zinc-500 mb-2">These are added on top of each employee's grade %. Ratings are applied in the Review tab.</div>
              <table className="hr-table">
                <thead><tr><th>Rating</th><th className="text-right">Bonus %</th><th>Interpretation</th></tr></thead>
                <tbody>
                  {cycle.rating_multipliers.map((rm, i) => (
                    <tr key={i}>
                      <td className="font-medium">{rm.rating}</td>
                      <td className={`text-right font-bold ${rm.bonus_percent > 0 ? "text-emerald-700" : rm.bonus_percent < 0 ? "text-rose-700" : "text-zinc-700"}`}>{rm.bonus_percent > 0 ? "+" : ""}{rm.bonus_percent}%</td>
                      <td className="text-xs text-zinc-500">{rm.bonus_percent > 0 ? "Pumps hike above grade default" : rm.bonus_percent < 0 ? "Reduces hike below grade default" : "Grade default applies"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Review */}
        <TabsContent value="review" className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="space-y-1"><Label className="text-xs">Search</Label><Input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} placeholder="Name / code" className="rounded-sm h-9 max-w-xs" data-testid="filter-q" /></div>
            <div className="space-y-1"><Label className="text-xs">Dept</Label>
              <select value={filter.department} onChange={(e) => setFilter({ ...filter, department: e.target.value })} className="rounded-sm border border-zinc-300 h-9 px-2 text-sm bg-white">
                <option value="">All</option>{uniqueDepts.map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Grade</Label>
              <select value={filter.grade} onChange={(e) => setFilter({ ...filter, grade: e.target.value })} className="rounded-sm border border-zinc-300 h-9 px-2 text-sm bg-white">
                <option value="">All</option>{uniqueGrades.map((g) => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Status</Label>
              <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="rounded-sm border border-zinc-300 h-9 px-2 text-sm bg-white">
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="manager_approved">Manager approved</option>
                <option value="hr_approved">HR approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            {priorCycles.length > 0 && (
              <div className="space-y-1"><Label className="text-xs">Compare vs</Label>
                <select value={comparePrevId} onChange={(e) => setComparePrevId(e.target.value)} className="rounded-sm border border-zinc-300 h-9 px-2 text-sm bg-white" data-testid="compare-prev-select">
                  <option value="">— None —</option>
                  {priorCycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="flex-1" />
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => bulk("manager_approve")} className="rounded-sm" data-testid="bulk-mgr-approve"><CheckCircle size={14} className="mr-1" /> Mgr approve</Button>
              {isHR && <Button variant="outline" size="sm" onClick={() => bulk("hr_approve")} className="rounded-sm bg-emerald-50 border-emerald-300 hover:bg-emerald-100" data-testid="bulk-hr-approve"><CheckCircle size={14} className="mr-1" /> HR approve</Button>}
              <Button variant="outline" size="sm" onClick={() => bulk("reject")} className="rounded-sm bg-rose-50 border-rose-300 hover:bg-rose-100" data-testid="bulk-reject"><XCircle size={14} className="mr-1" /> Reject</Button>
            </div>
          </div>

          {comparison && (
            <div className={`rounded-sm border p-3 text-xs ${comparison.high_variance_count > 0 ? "border-amber-300 bg-amber-50 text-amber-900" : "border-blue-300 bg-blue-50 text-blue-900"}`} data-testid="comparison-summary">
              Comparing against <strong>{comparison.prev_cycle.name}</strong> · {comparison.matched_count} employees matched · avg Δ {comparison.avg_delta > 0 ? "+" : ""}{comparison.avg_delta}% ·
              {comparison.high_variance_count > 0
                ? <> <Warning size={12} weight="fill" className="inline text-amber-600" /> {comparison.high_variance_count} with &gt;5pp swing</>
                : " all within 5pp"}
            </div>
          )}

          <div className="border border-zinc-200 rounded-sm bg-white overflow-x-auto" data-testid="items-list">
            <table className="hr-table">
              <thead>
                <tr>
                  <th className="w-10"><Checkbox checked={items.length > 0 && items.every((i) => selected[i.id])} onCheckedChange={(v) => toggleAll(!!v)} /></th>
                  <th>Employee</th><th>Dept · Grade</th>
                  <th className="text-right">Current (mo)</th>
                  <th className="text-right">Proposed (mo)</th>
                  <th className="text-right">Δ</th>
                  <th className="text-right">%</th>
                  {RATINGS.length > 0 && <th>Rating</th>}
                  {comparison && <th className="text-right">Prev %</th>}
                  <th className="text-right">New annual</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={comparison ? 11 : 10} className="text-center py-6 text-zinc-500 text-sm">No items. Populate first.</td></tr>}
                {items.map((i) => {
                  const delta = (i.proposed_new_ctc || 0) - (i.current_ctc || 0);
                  const comp = comparison?.matches.find((m) => m.item_id === i.id);
                  const compDelta = comp ? comp.delta : null;
                  return (
                    <tr key={i.id}>
                      <td><Checkbox checked={!!selected[i.id]} onCheckedChange={(v) => setSelected({ ...selected, [i.id]: !!v })} data-testid={`select-${i.employee_code}`} /></td>
                      <td><div className="text-sm">{i.employee_name}</div><div className="font-mono text-[10px] text-zinc-500">{i.employee_code}</div></td>
                      <td className="text-xs">{i.department} · <span className="font-mono">{i.grade}</span></td>
                      <td className="text-right text-xs">{INR(i.current_ctc)}</td>
                      <td className="text-right text-xs">
                        <input type="number" value={i.proposed_new_ctc || 0} onChange={(e) => patchItem(i.id, { proposed_new_ctc: parseFloat(e.target.value) })} className="rounded-sm border border-zinc-300 px-1 py-0.5 w-24 text-right text-xs" data-testid={`input-newctc-${i.employee_code}`} />
                      </td>
                      <td className={`text-right text-xs font-mono ${delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{delta >= 0 ? "+" : ""}{INR(delta)}</td>
                      <td className="text-right text-xs">
                        <input type="number" step="0.5" value={i.proposed_percent || 0} onChange={(e) => patchItem(i.id, { proposed_percent: parseFloat(e.target.value) })} className="rounded-sm border border-zinc-300 px-1 py-0.5 w-16 text-right text-xs" data-testid={`input-pct-${i.employee_code}`} />
                      </td>
                      {RATINGS.length > 0 && (
                        <td>
                          <select value={i.rating || ""} onChange={(e) => patchItem(i.id, { rating: e.target.value || null })} className="rounded-sm border border-zinc-300 h-7 px-1 text-xs bg-white" data-testid={`rating-${i.employee_code}`}>
                            <option value="">—</option>
                            {RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </td>
                      )}
                      {comparison && (
                        <td className="text-right text-xs">
                          {compDelta === null ? <span className="text-zinc-400">—</span> : (
                            <span className={`font-mono ${Math.abs(compDelta) > 5 ? "text-amber-700 font-bold" : "text-zinc-600"}`} title={`Last cycle: ${comp.prev_pct}%`}>
                              {comp.prev_pct}% <span className="text-[9px]">({compDelta > 0 ? "+" : ""}{compDelta}pp)</span>
                            </span>
                          )}
                        </td>
                      )}
                      <td className="text-right text-xs font-mono">{ANNUAL(i.proposed_new_ctc)}</td>
                      <td><Badge variant="outline" className={`rounded-sm text-[10px] ${ITEM_TONES[i.status]}`}>{i.status.replace("_", " ")}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Analytics */}
        <TabsContent value="analytics" className="mt-4 space-y-4" data-testid="tab-analytics-content">
          {!analytics && <div className="text-center py-16 text-zinc-500 text-sm">Loading analytics…</div>}
          {analytics && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="border border-zinc-200 rounded-sm p-3 bg-white"><div className="text-[11px] uppercase tracking-wider text-zinc-500">Total headcount</div><div className="font-display text-xl font-bold">{analytics.totals.count}</div></div>
                <div className="border border-emerald-300 rounded-sm p-3 bg-emerald-50"><div className="text-[11px] uppercase tracking-wider text-emerald-700">Annual delta</div><div className="font-display text-xl font-bold text-emerald-800">{INR(analytics.totals.delta_annual)}</div></div>
                <div className="border border-blue-300 rounded-sm p-3 bg-blue-50"><div className="text-[11px] uppercase tracking-wider text-blue-700">Avg / Min / Max %</div><div className="font-display text-lg font-bold text-blue-800">{analytics.totals.avg_pct}% · {analytics.totals.min_pct}% · {analytics.totals.max_pct}%</div></div>
                <div className="border border-amber-300 rounded-sm p-3 bg-amber-50"><div className="text-[11px] uppercase tracking-wider text-amber-700">Outliers</div><div className="font-display text-xl font-bold text-amber-800">{analytics.outliers.length}</div></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border border-zinc-200 rounded-sm bg-white p-4">
                  <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><ChartBar size={16} className="text-adb-navy" /> Cost by department (monthly)</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={analytics.by_department.slice(0, 12)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="key" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="current" name="Current" fill="#a1a1aa" />
                      <Bar dataKey="proposed" name="Proposed" fill="#1a4d8f" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="border border-zinc-200 rounded-sm bg-white p-4">
                  <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><ChartBar size={16} className="text-emerald-700" /> Cost by grade (monthly)</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={analytics.by_grade}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="key" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="current" name="Current" fill="#a1a1aa" />
                      <Bar dataKey="proposed" name="Proposed" fill="#059669" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="border border-zinc-200 rounded-sm bg-white p-4">
                <div className="text-sm font-semibold mb-2">Department rollup</div>
                <table className="hr-table">
                  <thead><tr><th>Department</th><th className="text-right">Headcount</th><th className="text-right">Current</th><th className="text-right">Proposed</th><th className="text-right">Δ (annual)</th><th className="text-right">Avg %</th><th className="text-right">Max %</th></tr></thead>
                  <tbody>
                    {analytics.by_department.map((r) => (
                      <tr key={r.key}>
                        <td className="text-sm">{r.key}</td>
                        <td className="text-right text-xs">{r.count}</td>
                        <td className="text-right text-xs font-mono">{INR(r.current)}</td>
                        <td className="text-right text-xs font-mono text-adb-navy">{INR(r.proposed)}</td>
                        <td className="text-right text-xs font-mono text-emerald-700">{INR(r.delta_annual)}</td>
                        <td className="text-right text-xs">{r.avg_pct}%</td>
                        <td className={`text-right text-xs ${r.max_pct > 25 ? "text-amber-700 font-bold" : "text-zinc-600"}`}>{r.max_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {analytics.outliers.length > 0 && (
                <div className="border border-amber-300 rounded-sm bg-amber-50 p-4" data-testid="analytics-outliers">
                  <div className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-amber-900"><Warning size={16} weight="fill" /> {analytics.outliers.length} outliers to review before publish</div>
                  <table className="hr-table">
                    <thead><tr><th>Employee</th><th>Dept · Grade</th><th className="text-right">Current</th><th className="text-right">Proposed</th><th className="text-right">%</th><th>Why flagged</th></tr></thead>
                    <tbody>
                      {analytics.outliers.map((o) => (
                        <tr key={o.item_id}>
                          <td className="text-xs"><div>{o.employee_name}</div><div className="font-mono text-[10px] text-zinc-500">{o.employee_code}</div></td>
                          <td className="text-xs">{o.department} · <span className="font-mono">{o.grade}</span></td>
                          <td className="text-right text-xs">{INR(o.current_ctc)}</td>
                          <td className="text-right text-xs">{INR(o.proposed_new_ctc)}</td>
                          <td className="text-right text-xs font-bold text-amber-800">{o.pct}%</td>
                          <td className="text-xs text-amber-800">{o.reasons.join(" · ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Publish */}
        <TabsContent value="publish" className="mt-4">
          <div className="border border-zinc-200 rounded-sm p-4 bg-white space-y-3">
            <div className="text-sm font-semibold">Ready to publish</div>
            <div className="text-sm text-zinc-600">
              <strong>{cycle.counts?.hr_approved || 0}</strong> HR-approved items will get new salary structures effective <strong>{cycle.effective_date}</strong>.
            </div>
            {cycle.status !== "published" ? (
              <Button onClick={publish} disabled={busy || (cycle.counts?.hr_approved || 0) === 0} className="btn-zenith rounded-sm" data-testid="publish-btn">
                <Coins size={16} className="mr-1.5" />
                {busy ? "Publishing…" : `Publish ${cycle.counts?.hr_approved || 0} increments`}
              </Button>
            ) : (
              <div className="text-sm text-emerald-800 flex items-center gap-2"><CheckCircle size={16} weight="fill" /> Published on {new Date(cycle.published_at).toLocaleDateString()} · {cycle.published_count} salary structures created</div>
            )}
          </div>
        </TabsContent>

        {/* Letters */}
        <TabsContent value="letters" className="mt-4">
          <div className="border border-zinc-200 rounded-sm p-4 bg-white space-y-3">
            <div className="text-sm font-semibold">One-click salary revision letters</div>
            <div className="text-sm text-zinc-600">Uses the <code className="text-adb-navy">salary_revision</code> template with each employee's new CTC, old CTC, percent hike, and effective date auto-filled.</div>
            {cycle.letters_batch_id ? (
              <div className="text-sm text-emerald-800 flex items-center gap-2"><CheckCircle size={16} weight="fill" /> Letters already issued · Batch <span className="font-mono">{cycle.letters_batch_id.slice(0, 8)}</span></div>
            ) : (
              <Button onClick={issueLetters} disabled={busy} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="issue-letters-btn">
                <PaperPlaneTilt size={16} className="mr-1.5" />
                {busy ? "Issuing…" : "Issue letters to all approved"}
              </Button>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Router entry (uses useParams)
// ============================================================
export default function IncrementCycles() {
  const { id } = useParams();
  return id ? <CycleDetail cid={id} /> : <CycleList />;
}
